import React from 'react';
import { EditOutlined, TeamOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons';
import { AddressEntityResponse, EventTypeEnum } from 'graphql/types';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import useEventAccessInfo from 'hooks/useEventAccessInfo';

import {
  StyledWrapper,
  StyledImgWrap,
  StyledDateBadge,
  StyledDateDay,
  StyledDateMonthYear,
  StyledBadgeRow,
  StyledContent,
  StyledBottom,
  StyledBottomWrap,
  StyledBottomText,
  StyledBottomContent,
  StyledImgTitle,
  StyledTitleRow,
  StyledActionButtons,
  StyledCardIconButton,
  StyledDescription,
  StyledMembersBadge,
} from './card.styled';

interface EventCardProps {
  coverPhotoUrl?: string;
  title?: string;
  description?: string;
  address?: AddressEntityResponse;
  group?: { data?: { attributes?: { name?: string | null } | null } | null } | null;
  startTime?: string;
  id?: string;
  editable?: boolean;
  isPast?: boolean;
  eventType?: EventTypeEnum;
  bookings?: { id?: string }[];
}

const EventCard = ({
  id,
  coverPhotoUrl,
  title,
  description,
  group,
  startTime,
  editable,
  isPast,
  bookings,
}: EventCardProps) => {
  const history = useHistory();
  const { isEventOrganizer } = useEventAccessInfo(id || '');
  const showOrganizerActions = editable ?? isEventOrganizer;
  const showManageBookings = showOrganizerActions && !isPast;

  const stopNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <StyledWrapper to={`/events/${id}`}>
      <StyledImgWrap url={coverPhotoUrl}>
        {startTime && (
          <StyledDateBadge>
            <StyledDateDay>{moment(startTime).format('DD')}</StyledDateDay>
            <StyledDateMonthYear>{moment(startTime).format('MMM YYYY').toUpperCase()}</StyledDateMonthYear>
          </StyledDateBadge>
        )}
        {showOrganizerActions && (
          <StyledBadgeRow>
            <StyledMembersBadge>
              <UserOutlined />
              {bookings ? bookings.length : 0}
            </StyledMembersBadge>
          </StyledBadgeRow>
        )}
      </StyledImgWrap>
      <StyledContent>
        <StyledTitleRow>
          <StyledImgTitle>{title}</StyledImgTitle>
          <StyledActionButtons>
            {showOrganizerActions ? (
              <StyledCardIconButton
                type="primary"
                ghost
                aria-label="Edit event"
                title="Edit event"
                onClick={(e: React.MouseEvent) => {
                  stopNavigation(e);
                  history.push(`/events/update/${id}`);
                }}
              >
                <EditOutlined />
              </StyledCardIconButton>
            ) : null}
            {showManageBookings ? (
              <StyledCardIconButton
                type="primary"
                ghost
                aria-label="Manage bookings"
                title="Manage bookings"
                onClick={(e: React.MouseEvent) => {
                  stopNavigation(e);
                  history.push(`/events/bookings/${id}`);
                }}
              >
                <UnorderedListOutlined />
              </StyledCardIconButton>
            ) : null}
          </StyledActionButtons>
        </StyledTitleRow>
        <StyledBottom>
          <StyledBottomContent>
            <StyledBottomWrap>
              <TeamOutlined />
              <StyledBottomText>{group?.data?.attributes?.name}</StyledBottomText>
            </StyledBottomWrap>
          </StyledBottomContent>
        </StyledBottom>
        {description && <StyledDescription dangerouslySetInnerHTML={{ __html: description }} />}
      </StyledContent>
    </StyledWrapper>
  );
};

export default EventCard;
